import React, { useEffect, useMemo, useRef, useState } from "react";
import { PanelGroup } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { Assignment } from "../../../api/Inventory/Entities/InventoryEntities";
import { MoveTypes } from "../../../api/Inventory/Entities/InventoryMovementEntities";
import Loader from "../../../components/Common/Loader";
import useInteractionGate from "../Hooks/useInteractionGate";
import useInventoryCart from "../Hooks/useInventoryCart";
import usePriorProtocolDispenses from "../Hooks/usePriorProtocolDispenses";
import { buildVisitTrackingRelationId, dateToSequenceNumber } from "../../../api/Common/Util";
import {
    buildReturnCatalog, deriveReDispenseRows,
    hydrateEntriesFromValue, normalizeMovementItems, remainingByOrigin
} from "../Hooks/protocolReturnRows";
import { buildCartKitReservation } from "../Shared/inventoryFormRules";
import { buildProtocolPatientDestination } from "../Shared/patientDestination";
import ProtocolItemField from "./ProtocolItemField";
import ProtocolReturnMovement from "./ProtocolReturnMovement";

export default function ProtocolImpFormField({ name, disabled, onChange, medicalRecordNumber, visitId, visitDate, protocolId, value }) {
    const { t } = useTranslation();
    const assignment = useMemo(() => Assignment.ProtocolAssigment(protocolId), [protocolId]);
    const dispenseValue = useMemo(() => firstDispense(value), [value]);
    const destination = useMemo(
        () => buildProtocolPatientDestination(medicalRecordNumber, visitId),
        [medicalRecordNumber, visitId]
    );
    const isPatientUnidentified = destination == null;
    const isReadOnly = disabled || isPatientUnidentified;
    const [dispenseMove, isMoveLoading, cart] = useInventoryCart(
        dispenseValue?.id ?? 0, MoveTypes.ProtocolDispense.Name,
        assignment.assignmentType, assignment.assignId,
        destination, dispenseValue
    );
    const persistedSignature = useMemo(
        () => (Array.isArray(value) ? value : [])
            .flatMap(m => (m.items ?? []).filter(i => (i.id ?? 0) > 0).map(i => `${i.id}:${i.originMoveItemId ?? ''}`))
            .sort().join('|'),
        [value]
    );
    const [comprobantes, isPriorLoading] = usePriorProtocolDispenses({
        protocolId, medicalRecordNumber, visitDate, currentMovementId: dispenseValue?.id ?? 0,
        persistedSignature
    });
    const [entries, setEntries] = useState([]);
    const entrySeq = useRef(0);
    const nextEntryKey = () => `e${++entrySeq.current}`;
    const [returnMove, setReturnMove] = useState(null);

    const catalog = useMemo(() => buildReturnCatalog(comprobantes, assignment), [comprobantes, assignment]);

    const returnsHydrated = useRef(false);
    useEffect(() => {
        if (returnsHydrated.current) return;
        if (comprobantes.length === 0) return;
        returnsHydrated.current = true;
        const adopted = persistedReturn(value, catalog);
        const seed = hydrateEntriesFromValue(catalog, adopted, firstDispense(value)?.items)
            .map(entry => ({ ...entry, entryKey: nextEntryKey() }));
        if (seed.length > 0) setEntries(seed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comprobantes]);

    const { hasInteracted, markInteracted } = useInteractionGate();

    const onAddEntry = (originMoveItemId, draft) => {
        const line = catalog.find(l => l.originMoveItemId === originMoveItemId);
        if (line == null) return;
        markInteracted();
        setEntries(prev => [...prev, { ...line, entryKey: nextEntryKey(), ...draft }]);
    };
    const onRemoveEntry = entryKey => {
        markInteracted();
        setEntries(prev => prev.filter(e => e.entryKey !== entryKey));
    };
    const onUnRedispense = entryKey => {
        markInteracted();
        setEntries(prev => prev.map(e => e.entryKey === entryKey ? { ...e, reDispense: false } : e));
    };
    const onReDispenseEntry = entryKey => {
        markInteracted();
        setEntries(prev => prev.map(e => e.entryKey === entryKey ? { ...e, reDispense: true } : e));
    };
    const onReDispenseReturned = (returnedItem) => {
        if (returnedItem?.originMoveItemId == null) return;
        markInteracted();
        cart.AddItem(
            returnedItem.item, returnedItem.release, 1, returnedItem.assignment,
            returnedItem.partialQuantity ?? 1, null, returnedItem.originMoveItemId,
            returnedItem.partialQuantity ?? null, null, null, returnedItem.dispensedKitQuantity ?? null
        );
    };

    const onMoveChange = move => setReturnMove(move);

    const handleNewItem = item => {
        markInteracted();
        cart.AddItem(item.item, item.release, item.quantity * 1, item.assignment, item.availableQuantity, item.comment);
    };
    const onRemoveItem = item => {
        markInteracted();
        cart.RemoveItem(item);
    };

    const reDispenseRows = useMemo(() => deriveReDispenseRows(entries), [entries]);

    const reDispenseTracked = useRef(new Map());
    useEffect(() => {
        if (isPriorLoading) return;
        const live = new Map(reDispenseRows.map(r => [r.key, r]));

        const trackedIds = () => new Set(Array.from(reDispenseTracked.current.values()).map(x => x.uniqueId));
        const adopted = trackedIds();
        const pool = new Map();
        for (const item of (dispenseMove?.items ?? [])) {
            if (item.originMoveItemId == null || adopted.has(item.uniqueId)) continue;
            const list = pool.get(item.originMoveItemId) ?? [];
            list.push(item);
            pool.set(item.originMoveItemId, list);
        }
        for (const [key, r] of live) {
            if (reDispenseTracked.current.has(key)) continue;
            const candidates = pool.get(r.originMoveItemId);
            if (candidates?.length) {
                const item = candidates.shift();
                reDispenseTracked.current.set(key, { uniqueId: item.uniqueId, partialQuantity: item.partialQuantity ?? r.partialQuantity });
            }
        }

        for (const [key, tracked] of reDispenseTracked.current) {
            const r = live.get(key);
            if (r != null && r.partialQuantity === tracked.partialQuantity) continue;
            cart.RemoveItem({ uniqueId: tracked.uniqueId });
            reDispenseTracked.current.delete(key);
        }
        for (const [key, r] of live) {
            if (reDispenseTracked.current.has(key)) continue;
            const uniqueId = cart.AddItem(
                r.item, r.release, 1, r.assignment, r.partialQuantity, null,
                r.originMoveItemId, r.partialQuantity, null, null, r.dispensedKitQuantity
            );
            reDispenseTracked.current.set(key, { uniqueId, partialQuantity: r.partialQuantity });
        }

        const knownOrigins = new Set(catalog.map(l => l.originMoveItemId));
        const keep = trackedIds();
        for (const item of (dispenseMove?.items ?? [])) {
            if (item.originMoveItemId == null) continue;
            if (!knownOrigins.has(item.originMoveItemId)) continue;
            if (keep.has(item.uniqueId)) continue;
            cart.RemoveItem({ uniqueId: item.uniqueId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reDispenseRows, dispenseMove, catalog, isPriorLoading]);

    const dispenseItems = useMemo(
        () => (dispenseMove?.items ?? []).filter(i => i.originMoveItemId == null),
        [dispenseMove]
    );
    const reDispenseItems = useMemo(
        () => (dispenseMove?.items ?? []).filter(i => i.originMoveItemId != null),
        [dispenseMove]
    );

    const entryOrigins = useMemo(() => new Set(entries.map(e => e.originMoveItemId)), [entries]);
    const returnedItems = useMemo(
        () => (returnMove?.items ?? []).filter(i => !entryOrigins.has(i.originMoveItemId)),
        [returnMove, entryOrigins]
    );
    const lockedReturnOrigins = useMemo(
        () => new Set(reDispenseItems.map(i => i.originMoveItemId)),
        [reDispenseItems]
    );

    const relation = useMemo(() => {
        const relatedId = protocolId && medicalRecordNumber
            ? buildVisitTrackingRelationId(medicalRecordNumber, protocolId)
            : null;
        const sequenceNumber = dateToSequenceNumber(visitDate);
        return relatedId && sequenceNumber ? { relatedId, sequenceNumber } : null;
    }, [protocolId, medicalRecordNumber, visitDate]);

    const adoptedReturn = useMemo(() => {
        const matched = persistedReturn(value, catalog);
        if (matched != null) return matched;
        if (isPriorLoading || !Array.isArray(value)) return null;
        return value.find(m => isReturnMove(m) && !isCanceledMove(m)
            && ((m.items?.length ?? 0) > 0 || (m.id ?? 0) > 0)) ?? null;
    }, [value, catalog, isPriorLoading]);

    const passThroughReturns = useMemo(() => {
        if (!Array.isArray(value) || isPriorLoading) return [];
        return value.filter(m =>
            isReturnMove(m) && (m.items?.length ?? 0) > 0 && m !== adoptedReturn);
    }, [value, adoptedReturn, isPriorLoading]);

    const passThroughCounts = useMemo(() => {
        const counts = new Map();
        for (const move of passThroughReturns) {
            for (const item of (move.items ?? [])) {
                if (item.originMoveItemId == null) continue;
                counts.set(item.originMoveItemId, (counts.get(item.originMoveItemId) ?? 0) + 1);
            }
        }
        return counts;
    }, [passThroughReturns]);
    const remaining = useMemo(
        () => remainingByOrigin(catalog, entries, passThroughCounts),
        [catalog, entries, passThroughCounts]
    );
    const passThroughRef = useRef([]);
    passThroughRef.current = passThroughReturns;
    const passThroughSignature = useMemo(
        () => passThroughReturns.map(m =>
            `${m.originDispenseMovementId}:${(m.items ?? []).map(i => `${i.originMoveItemId}:${i.partialQuantity}`).join(',')}`).join('|'),
        [passThroughReturns]
    );

    const loadedItemCount = (dispenseValue?.items ?? []).filter(i => i.originMoveItemId == null).length;
    const loadedReturnItemCount = useMemo(
        () => Array.isArray(value)
            ? value.filter(isReturnMove).reduce((s, m) => s + (m.items?.length ?? 0), 0)
            : 0,
        [value]
    );

    const emitHydrated = useRef(false);
    useEffect(() => {
        const passThrough = passThroughRef.current;
        const cartReturnItemCount = returnMove?.items?.length ?? 0;

        if (!emitHydrated.current) {
            const plainCartCount = (dispenseMove?.items ?? []).filter(i => i.originMoveItemId == null).length;
            const dispenseReflected = loadedItemCount === 0 || plainCartCount >= loadedItemCount;
            const passThroughItemCount = passThrough.reduce((s, m) => s + (m.items?.length ?? 0), 0);
            const returnsReflected = isPriorLoading
                ? loadedReturnItemCount === 0
                : cartReturnItemCount + passThroughItemCount >= loadedReturnItemCount;
            if (!dispenseReflected || !returnsReflected) return;
        }
        emitHydrated.current = true;
        if (!hasInteracted()) return;
        const cartCoversPersisted = (returnMove?.id ?? 0) > 0;
        const emptiedPersistedReturns = !cartCoversPersisted && Array.isArray(value)
            ? value.filter(m => isReturnMove(m) && !isCanceledMove(m)
                && (m.id ?? 0) > 0 && (m.items?.length ?? 0) === 0)
            : [];
        const returnMoves = cartReturnItemCount > 0 || cartCoversPersisted
            ? [returnMove]
            : emptiedPersistedReturns;
        const dispense = { ...dispenseMove };
        const batch = [...passThrough, ...returnMoves, dispense].map(move => ({
            ...move,
            relation: move.relation ?? relation,
            items: normalizeMovementItems(move.items, assignment)
        }));
        onChange(name, batch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [returnMove, dispenseMove, relation, passThroughSignature, isPriorLoading,
        loadedItemCount, loadedReturnItemCount, assignment, name, onChange]);

    return (
        <Loader isLoading={isMoveLoading || isPriorLoading} name={name}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', backgroundColor: 'white', padding: '10px' }}>
                <div style={{ textAlign: 'center' }}>
                    <h5><strong>{t('inventory.dispense.protocolDispenseTitle')}</strong></h5>
                    <hr />
                </div>
                {isPatientUnidentified && (
                    <p className="text-muted" style={{ textAlign: 'center' }}>
                        {t('inventory.dispense.patientVisitDataMissing')}
                    </p>
                )}
                <PanelGroup accordion id="multi-items-pharmacy" defaultActiveKey="multi-item--1">
                    {dispenseItems.map((item, index) => (
                        <ProtocolItemField key={`disp-${item.uniqueId ?? index}`} index={`${index}`} movementItem={item} disabled={true}
                            assignmentType={assignment.assignmentType} assignId={assignment.assignId} itemType="Protocol"
                            handleSubmit={handleNewItem} handleRemove={onRemoveItem}
                            hasActions={true} actionsDisbled={isReadOnly} isDispensed={dispenseMove?.id > 0}
                            takenKits={buildCartKitReservation(dispenseMove?.items, item)} />
                    ))}
                    {reDispenseItems.map((item, index) => (
                        <ProtocolItemField key={`redisp-${item.uniqueId ?? index}`} index={`redisp-${index}`} movementItem={item}
                            disabled={true} locked={true} assignmentType={assignment.assignmentType} assignId={assignment.assignId} itemType="Protocol"
                            handleRemove={(cartItem) => {
                                const row = reDispenseRows.find(r => r.originMoveItemId === cartItem?.originMoveItemId);
                                if (row != null) onUnRedispense(row.entryKey);
                                else cart.RemoveItem({ uniqueId: cartItem?.uniqueId });
                            }}
                            hasActions={true} isDispensed={true} />
                    ))}
                    {!isReadOnly && (
                        <ProtocolItemField index="-1" movementItem={null} disabled={isReadOnly}
                            assignmentType={assignment.assignmentType} assignId={assignment.assignId} itemType="Protocol"
                            handleSubmit={handleNewItem} bsStyle="info"
                            takenKits={buildCartKitReservation(dispenseMove?.items)} />
                    )}
                </PanelGroup>
            </div>

            {(comprobantes.length > 0 || adoptedReturn != null
                || (returnMove?.id ?? 0) > 0 || (returnMove?.items?.length ?? 0) > 0) && (
                <div className="protocol-return__section">
                    <div className="protocol-return__section-title">
                        <h5><strong>{t('inventory.dispense.protocolReturnTitle')}</strong></h5>
                        <hr />
                    </div>
                    <ProtocolReturnMovement
                        key={`return-cart-${adoptedReturn?.id || returnMove?.id || 0}`}
                        entries={entries} comprobantes={comprobantes} remaining={remaining}
                        persistedMove={adoptedReturn}
                        assignment={assignment} destination={destination}
                        disabled={isReadOnly} onMoveChange={onMoveChange} onUserEdit={markInteracted}
                        onAddEntry={onAddEntry} onRemoveEntry={onRemoveEntry}
                        onReDispenseReturned={onReDispenseReturned} onReDispenseEntry={onReDispenseEntry}
                        returnedItems={returnedItems} lockedOrigins={lockedReturnOrigins} />
                </div>
            )}
        </Loader>
    );
}

function isReturnMove(move) {
    return MoveTypes.IsEquivalent(move?.moveType, MoveTypes.ProtocolReturn);
}

function isCanceledMove(move) {
    return `${move?.status ?? ''}`.toLowerCase() === 'canceled';
}


function firstDispense(value) {
    if (value == null) return null;
    if (Array.isArray(value)) {
        return value.find(m => MoveTypes.IsEquivalent(m?.moveType, MoveTypes.ProtocolDispense)) ?? null;
    }
    return value;
}

function persistedReturn(value, rows) {
    if (!Array.isArray(value)) return null;
    const returns = value.filter(isReturnMove);
    return returns.find(m => (m?.items ?? []).some(it =>
            it?.originMoveItemId != null && rows.some(r => r.originMoveItemId === it.originMoveItemId)))
        ?? returns.find(m => m?.originDispenseMovementId != null
            && rows.some(r => r.originDispenseMovementId === m.originDispenseMovementId))
        ?? null;
}
